import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, Injector } from "@angular/core";
import { initialize as createLaunchDarklyClient } from "launchdarkly-js-client-sdk";
import { FeatureService, featureServiceInjectionToken, Guid, IMooGlobal, launchDarklyInitializer, mooGlobalInjectionToken } from "moo-shared";
import { Observable, tap } from "rxjs";
import { WINDOW } from "../../shared/app.window";
import { EnvironmentService } from "../environment.service";
import { LoginType } from "./authentication-server.service";
import { IIpAccessTokenDto } from "./ip-access-token.dto";
import { IIpOrganisationDto } from "./ip-organisation.dto";
import { IpTokenService } from "./iptoken.service";
import { ILoginPictureDto } from "./login-picture.dto";
import { OidcService } from "./oidc.service";

const authApiUrlPattern = "https://apis@env.moo.nl/authenticationapi/api";
const defaultOrganisationKey = "00000000-0000-0000-0000-000000000000" as Guid;
@Injectable()
export class AuthenticationApiService {
  private ipTokenService?: IpTokenService;

  stsUrl = "https://sts.moo.nl/websts";
  authApiPath = "";

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly httpClient: HttpClient,
    private readonly oidcService: OidcService,
    private readonly injector: Injector,
    @Inject(WINDOW) private readonly window: Window,
    @Inject(mooGlobalInjectionToken) private readonly moo: IMooGlobal,
    @Inject(featureServiceInjectionToken) private readonly featureService: FeatureService
  ) {
    this.getAuthApiUrl();
  }

  private getIpTokenService(): IpTokenService {
    if (!this.ipTokenService) {
      this.ipTokenService = this.injector.get(IpTokenService);
    }
    return this.ipTokenService;
  }

  private initializeLaunchDarkly(): void {
    launchDarklyInitializer(createLaunchDarklyClient).useFactory(this.moo, this.featureService)();
  }

  getOrganisationsBasedOnIp(): Observable<IIpOrganisationDto[]> {
    const requestUrl = this.authApiPath + "/ip/organisations";
    return (this.environmentService.isNonProdEnv()
      ? this.httpClient.get<IIpOrganisationDto[]>(requestUrl, { withCredentials: true })
      : this.httpClient.get<IIpOrganisationDto[]>(requestUrl));
  }

  resetLaunchDarklyOrganisationContext(): void {
    this.moo.launchDarklyProperties.OrganisationKey = defaultOrganisationKey;
    this.initializeLaunchDarkly();
  }

  getTokenForOrganisation(organisationKey: Guid): Observable<IIpAccessTokenDto> {
    const requestUrl = this.authApiPath + `/ip/organisations/${organisationKey}/token`;
    return (this.environmentService.isNonProdEnv()
      ? this.httpClient.post<IIpAccessTokenDto>(requestUrl, {}, { withCredentials: true })
      : this.httpClient.post<IIpAccessTokenDto>(requestUrl, {}))
      .pipe(
        tap(() => this.moo.launchDarklyProperties.OrganisationKey = organisationKey.toLowerCase()),
        tap(() => this.initializeLaunchDarkly())
      );
  }

  getAuthApiUrl() {
    const env = this.environmentService.getEnvironmentTag();
    this.authApiPath = authApiUrlPattern.replace("@env", env);
  }

  updatePassword(userName: string, oldPassword: string, newPassword: string, googleAccessToken?: string): Observable<unknown> {
    const encodedUserName = encodeURIComponent(userName);
    const requestUrl = `${this.authApiPath}/users/loginname/${encodedUserName}/textpassword`;
    const body: Record<string, string> = { OldPassword: oldPassword, NewPassword: newPassword };

    if (googleAccessToken) {
      body["GoogleAccessToken"] = googleAccessToken;
    }

    return this.httpClient.patch<string>(requestUrl, body);
  }

  loginWithToken(token: string, cacheKey: string) {
    const requestUrl = this.stsUrl + `/Account/LoginAsSelf/?CacheKey=${cacheKey}&access_token=${token}`;
    this.window.location.href = requestUrl;
  }

  getLoginUrl(username: string): Observable<string> {
    let requestUrl = this.getUserClientUrl(username) + "/loginUrl";
    if (this.oidcService.isClientConfigAvailable) {
      requestUrl += "?useNewAadFlow=true";
    }
    return this.httpClient.get<string>(requestUrl, {
      headers: {
        SPINNER_DISABLE_HEADER: "true"
      }
    });
  }

  getPictureLoginOptions(username: string): Observable<{ Key: string, Name: string }[]> {
    const requestUrl = this.getUserClientUrl(username) + "/passwordpictures";
    return this.httpClient.get<ILoginPictureDto[]>(requestUrl);
  }

  private getUserClientUrl(userName: string): string {
    const encodedUsername = encodeURIComponent(userName);
    return this.authApiPath + `/users/${encodedUsername}`;
  }

  getGroups(organisationKey: Guid): Observable<IAuthApiGroupDto[]> {
    const url = `${this.authApiPath}/organisations/${organisationKey}/groups`;
    return this.httpClient.get<IAuthApiGroupDto[]>(url,
      {
        headers: this.addAuthorizationHeader()
      });
  }

  private addAuthorizationHeader(headers: HttpHeaders = new HttpHeaders({})): HttpHeaders {
    return headers.append("Authorization", `Bearer ${this.getIpTokenService().currentToken}`);
  }
}

export interface ISecretDto {
  QrCodeKey: Guid;
  QrCodeVersionKey: Guid;
  PictureKeys: Guid[];
}
export interface IAuthApiGroupDto {
  Key: Guid;
  LoginType: LoginType;
}

export interface IQrCodeValidatedDto {
  LoginName: string;
  PasswordPictures: ILoginPictureDto[]
}
